import React, { useState, useEffect, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import Joi from "joi-browser";
import { toast } from "react-toastify";
import moment from "moment";
import Input from "../common/Input";
import Select from "../common/Select";
import {
  getBintiCaseNotes,
  getBintiCaseNotesForUAP,
  postBintiCaseNote,
} from "../services/soar";

function BintiCaseNotes({ bintiId, application }) {
  const isMountedRef = useRef(true);
  const [notes, setNotes] = useState([]);
  const [types, setTypes] = useState([]);
  // const [userAgencyIds, setUserAgencyIds] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("");
  const [errors, setErrors] = useState({});
  const [data, setData] = useState({});

  const state = application.HomeData[0].company;
  const applicantId =
    application.HomeData[0]?.members.filter(
      (m) => m.primarycontact === "Yes"
    )[0]?.contactid || null;

  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  const joiSchema = {
    date: Joi.string().required().label("Date"),
    "case-note-type": Joi.string().required().label("Type"),
    "contact-method": Joi.string()
      .label("Contact Method")
      .when("case-note-type", {
        is: Joi.any().valid(
          "case_update",
          "other",
          "recruiting",
          "family_evaluation"
        ),
        then: Joi.required(),
        otherwise: Joi.allow(""),
      }),
    "case-note-body": Joi.string().required().label("Note"),
    "estimated-approval-date": Joi.string()
      .label("Estimated Approval Date")
      .when("case-note-type", {
        is: Joi.any().valid("approval_date_estimate"),
        then: Joi.required(),
        otherwise: Joi.allow(""),
      }),
    nextContactDate: Joi.string().allow(""),
    nextContactMethod: Joi.string().when("nextContactDate", {
      is: Joi.any().valid("", null),
      then: Joi.allow(""),
      otherwise: Joi.required(),
    }),
    nextContactBody: Joi.string().when("nextContactDate", {
      is: Joi.any().valid("", null),
      then: Joi.allow(""),
      otherwise: Joi.required(),
    }),
  };

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  const showCaseNotes = () => {
    setIsOpen(true);
    loadCaseNotes();
  };

  const loadCaseNotes = async () => {
    if (application?.HomeData[0]?.noLicenseId === "1") {
      getBintiCaseNotes(state, "1", bintiId, "null").then((response) => {
        setLoaded(true);
        if (response && isMountedRef.current) {
          const data = JSON.parse(response).casenotes.sort((a, b) => {
            const aDate = new Date(a.attributes["date"]);
            const bDate = new Date(b.attributes["date"]);
            if (aDate < bDate) return 1;
            if (aDate > bDate) return -1;
            return 0;
          });
          const unique = [
            ...new Set(data.map((note) => note.attributes["case-note-type"])),
          ];
          setNotes(data);
          setTypes(unique);
        }
      });
    } else {
      getBintiCaseNotesForUAP(applicantId).then((response) => {
        console.log("Binti UAP Case Notes");
        setLoaded(true);
        if (response && isMountedRef.current) {
          const data = JSON.parse(response)
            .casenotes.map((cn) => ({
              id: cn.id.toString(),
              type: cn["case-note-type"],
              attributes: cn,
              relationships: {
                caseworker: {
                  data: {
                    id: cn["agency-worker-id"].toString(),
                  },
                },
              },
            }))
            .sort((a, b) => {
              const aDate = new Date(a.attributes["date"]);
              const bDate = new Date(b.attributes["date"]);
              if (aDate < bDate) return 1;
              if (aDate > bDate) return -1;
              return 0;
            });

          const unique = [
            ...new Set(data.map((note) => note.attributes["case-note-type"])),
          ];

          setNotes(data);
          setTypes(unique);
        }
      });
    }
  };

  const handleChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) {
      errs[input.name] = errorMessage;
      console.log("Errors:", JSON.stringify(errs));
    } else {
      delete errs[input.name];
    }

    const obj = { ...data };
    obj[input.name] = input.value;
    setData(obj);
    setErrors(errs);
  };

  const addCaseNote = async () => {
    if (!validate()) {
      if (
        process.env.REACT_APP_ENVIRONMENT === "Production" ||
        bintiId === "384446" ||
        bintiId === "381389"
      ) {
        const toastId = toast.loading("Writing to Binti", {
          type: toast.TYPE.INFO,
        });
        handleSave(toastId);
      } else {
        toast.success("This would have created a case note in Binti");
      }
    }
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(data, joiSchema, options);
    let validationErrors = {};

    if (error?.details) {
      for (let item of error.details)
        validationErrors[item.path[0]] = item.message;
    }

    if (data.nextContactDate) {
      const now = moment().startOf("day");
      const val = moment(data.nextContactDate).startOf("day");

      if (val.isBefore(now)) {
        validationErrors.nextContactDate =
          '"Next Contact Date" cannot be in the past';
      }
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length > 0 ? validationErrors : null;
  };

  const validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    const { error } = Joi.validate(obj, schema);
    return error ? error.details[0].message : null;
  };

  const workerId = () => {
    // switch (state.toLowerCase()) {
    //   case "alabama":
    //     return "981900";
    //   case "ohio":
    //     return "981889";
    //   case "nevada":
    //     return "981893";
    //   case "southcarolina":
    //     return "981895";
    //   case "kentucky":
    //     return "981897";
    //   case "colorado":
    //     return "1120512";
    //   case "indiana":
    //     return "1120518";
    // }

    const ids = safyInfo.user.safyInfo.binti.filter(
      (i) => i.company === state.toLowerCase()
    );
    return ids.length === 1 ? ids[0].id : null;
  };

  const handleSave = (toastId) => {
    const obj = {
      amount_spent: null,
      case_note_body: `${
        JSON.parse(localStorage.getItem("safyInfo")).user.displayName
      }-${data["case-note-body"].replaceAll('"', "'").replaceAll("\n", " ")}`,
      case_note_type: data["case-note-type"],
      contact_method:
        data["contact-method"] !== "" ? data["contact-method"] : null,
      date: data["date"] !== "" ? data["date"] : null,
      estimated_approval_date:
        data["estimated-approval-date"] !== ""
          ? data["estimated-approval-date"]
          : null,
      support_service_type: null,
      agency_worker_id: workerId(),
      adult_profile_id: applicantId,
      child_id: null,
    };

    const nextContactObj = {
      amount_spent: null,
      case_note_body: `${
        JSON.parse(localStorage.getItem("safyInfo")).user.displayName
      } - ${
        data.nextContactBody
          ? data.nextContactBody.replaceAll('"', "'").replaceAll("\n", " ")
          : null
      }`,
      case_note_type: "next_planned_contact",
      contact_method:
        data.nextContactMethod !== "" ? data.nextContactMethod : null,
      date: data.nextContactDate,
      estimated_approval_date: null,
      support_service_type: null,
      agency_worker_id: workerId(),
      adult_profile_id: applicantId,
      child_id: null,
    };

    // const obj = {
    //   type: "case-notes",
    //   data: {
    //     attributes: {
    //       "case-note-body": `${
    //         JSON.parse(localStorage.getItem("safyInfo")).user.displayName
    //       }-${data["case-note-body"]}`,
    //       "case-note-type": "other",
    //       "contact-method": "contact_other",
    //       date: data.date,
    //     },
    //     relationships: {
    //       caseworker: {
    //         data: {
    //           id: workerId(),
    //           type: "agency-workers",
    //         },
    //       },
    //       "adult-profile": {
    //         data: {
    //           id: "991601",
    //           type: "user-agency-profiles",
    //         },
    //       },
    //       child: {
    //         data: {},
    //       },
    //     },
    //     meta: {},
    //   },
    // };

    console.log("Case Note Obj", obj);

    postBintiCaseNote(state, obj).then((resp) => {
      console.log("Post Case Note Resp", resp);
      const noteResp = JSON.parse(resp);
      if (noteResp.SystemResponse?.Information[0]?.Code === "SUCCESS") {
        const toastMessage = (
          <div className="border rounded p-1">
            <div className="bg-primary text-white p-2">{`Case Note ID ${noteResp.SystemResponse.Information[0].Id}`}</div>
            <div className="bg-white text-dark p-2">
              <div>Case Note Created in:</div>
              <div>
                <i className="fa-solid fa-circle-check text-success me-2"></i>
                <div className="d-inline text-dark">Binti</div>
              </div>
              {noteResp.SystemResponse?.Information[1]?.Code === "SUCCESS" ? (
                <div>
                  <i className="fa-solid fa-circle-check text-success me-2"></i>
                  <div className="d-inline text-dark">Portal</div>
                </div>
              ) : (
                <div>
                  <i className="fa-solid fa-circle-xmark text-danger me-2"></i>
                  <div className="d-inline text-dark">Portal</div>
                </div>
              )}
            </div>
          </div>
        );
        toast.update(toastId, {
          render: toastMessage,
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
        });
        if (!data.nextContactDate || data.nextContactDate === "") {
          clearData();
          showCaseNotes();
          setAddOpen(false);
        } else {
          postBintiCaseNote(state, nextContactObj).then((resp2) => {
            console.log("Posted Next Contact Date Note", resp2);
            const nextNoteResp = JSON.parse(resp2);
            if (
              nextNoteResp.SystemResponse?.Information[0]?.Code === "SUCCESS"
            ) {
              const nextToastMessage = (
                <>
                  <div className="border rounded p-1">
                    <div className="bg-primary text-white p-2">{`Case Note ID ${noteResp.SystemResponse.Information[0].Id}`}</div>
                    <div className="bg-white text-dark p-2">
                      <div>Case Note Created in:</div>
                      <div>
                        <i className="fa-solid fa-circle-check text-success me-2"></i>
                        <div className="d-inline text-dark">Binti</div>
                      </div>
                      {noteResp.SystemResponse?.Information[1]?.Code ===
                      "SUCCESS" ? (
                        <div>
                          <i className="fa-solid fa-circle-check text-success me-2"></i>
                          <div className="d-inline text-dark">Portal</div>
                        </div>
                      ) : (
                        <div>
                          <i className="fa-solid fa-circle-xmark text-danger me-2"></i>
                          <div className="d-inline text-dark">Portal</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="border rounded p-1">
                    <div className="bg-primary text-white p-2">{`Next Case Note ID ${nextNoteResp.SystemResponse.Information[0].Id}`}</div>
                    <div className="bg-white text-dark p-2">
                      <div>Next Case Note Created in:</div>
                      <div>
                        <i className="fa-solid fa-circle-check text-success me-2"></i>
                        <div className="d-inline text-dark">Binti</div>
                      </div>
                      {nextNoteResp.SystemResponse?.Information[1]?.Code ===
                      "SUCCESS" ? (
                        <div>
                          <i className="fa-solid fa-circle-check text-success me-2"></i>
                          <div className="d-inline text-dark">Portal</div>
                        </div>
                      ) : (
                        <div>
                          <i className="fa-solid fa-circle-xmark text-danger me-2"></i>
                          <div className="d-inline text-dark">Portal</div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
              toast.update(toastId, {
                render: nextToastMessage,
                type: toast.TYPE.SUCCESS,
                isLoading: false,
                autoClose: 5000,
                closeOnClick: true,
              });
              clearData();
              showCaseNotes();
              setAddOpen(false);
            } else {
              toast.update(toastId, {
                render: `Next Contact failed with the message ${nextNoteResp.SystemResponse.Information[0].Message}`,
                type: toast.TYPE.ERROR,
                isLoading: false,
                autoClose: 5000,
                closeOnClick: true,
              });
            }
          });
        }
      } else {
        toast.update(toastId, {
          render: `Case Note failed with the message ${noteResp.SystemResponse.Information[0].Message}}`,
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
        });
      }
    });
  };

  const clearData = () => {
    setData({
      "case-note-type": "",
      date: "",
      "case-note-body": "",
      "estimated-approval-date": "",
    });
    setErrors({});
  };

  return (
    <div>
      <Modal
        size="xl"
        show={addOpen}
        animation={false}
        onHide={() => {
          setAddOpen(false);
          setIsOpen(true);
          clearData();
        }}
      >
        <Modal.Header closeButton className="bg-safy-info text-white">
          <Modal.Title>{`Add Case Note for ${application.HomeData[0].fostercarehomename}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {process.env.REACT_APP_ENVIRONMENT !== "Production" &&
          bintiId !== "384446" ? (
            <div className="bg-danger rounded text-white p-2 mb-4">
              Since the Binti system is live data and this is the Test
              environment, this Case Note will not be officially posted. This is
              for demonstration and devlopment purposes only
            </div>
          ) : null}
          <div className="row">
            <div className="col-4">
              <Input
                name="date"
                label="Date"
                value={data.date}
                onChange={handleChange}
                error={errors.date}
                type="date"
              ></Input>
            </div>
            <div className="col-4"></div>
            <div className="col-4">
              {/* <Select
                name="uap"
                label="Case Note for"
                value={data.uap}
                onChange={handleChange}
                error={errors.uap}
                options={userAgencyIds}
              /> */}
              <Select
                name="case-note-type"
                label="Type"
                value={data["case-note-type"]}
                onChange={handleChange}
                error={errors["case-note-type"]}
                options={[
                  {
                    name: "Approval Date Estimate",
                    _id: "approval_date_estimate",
                  },
                  { name: "Case Update", _id: "case_update" },
                  {
                    name: "Family Evaluation/Home Study",
                    _id: "family_evaluation",
                  },
                  { name: "Recruiting", _id: "recruiting" },
                  { name: "Other", _id: "other" },
                  // { name: "Email", _id: "email" },
                  // { name: "Phone Call", _id: "phone_call" },
                  // { name: "Visit", _id: "visit" },
                  { name: "Training", _id: "training" },
                  {
                    name: "Renewal Progress Update",
                    _id: "renewal_progress_update",
                  },
                ]}
              ></Select>
            </div>
          </div>
          <div className="row">
            {data["case-note-type"] === "case_update" ||
            data["case-note-type"] === "next_planned_contact" ||
            data["case-note-type"] === "other" ||
            data["case-note-type"] === "recruiting" ||
            data["case-note-type"] === "family_evaluation" ? (
              <div className="col-4">
                <Select
                  name="contact-method"
                  label="Contact Method"
                  value={data["contact-method"]}
                  onChange={handleChange}
                  error={errors["contact-method"]}
                  options={[
                    { name: "Email", _id: "contact_email" },
                    { name: "Phone Call", _id: "contact_phone_call" },
                    { name: "Visit", _id: "contact_visit" },
                    { name: "Written Letter", _id: "contact_written_letter" },
                    // { name: "Attempted Visit", _id: "contact_attempted_visit" },
                    // { name: "Scheduled Visit", _id: "contact_scheduled_visit" },
                    // {
                    //   name: "Case Management Meeting",
                    //   _id: "contact_case_management_meeting",
                    // },
                    { name: "Text Message", _id: "contact_text_message" },
                    {
                      name: "Video Conference",
                      _id: "contact_video_conference",
                    },
                    { name: "Other", _id: "contact_other" },
                  ]}
                ></Select>
              </div>
            ) : null}
            {data["case-note-type"] === "approval_date_estimate" ? (
              <div className="col-4">
                <Input
                  name="estimated-approval-date"
                  label="Estimated Approval Date"
                  value={data["estimated-approval-date"]}
                  onChange={handleChange}
                  error={errors["estimated-approval-date"]}
                  type="date"
                ></Input>
              </div>
            ) : null}
          </div>
          <Input
            name="case-note-body"
            label="Case Note"
            rows="4"
            value={data["case-note-body"]}
            onChange={handleChange}
            error={errors["case-note-body"]}
          />
          <div className="border bg-light p-2">
            <div className="row">
              <div className="col-4">
                <Input
                  name="nextContactDate"
                  label="Next Contact Date"
                  value={data["nextContactDate"]}
                  onChange={handleChange}
                  error={errors["nextContactDate"]}
                  type="date"
                />
              </div>
              <div className="col-4"></div>
              <div className="col-4">
                <Select
                  name="nextContactMethod"
                  label="Next Contact Method"
                  value={data["nextContactMethod"]}
                  onChange={handleChange}
                  error={errors["nextContactMethod"]}
                  options={[
                    { name: "Email", _id: "contact_email" },
                    { name: "Phone Call", _id: "contact_phone_call" },
                    { name: "Visit", _id: "contact_visit" },
                    { name: "Written Letter", _id: "contact_written_letter" },
                    { name: "Text Message", _id: "contact_text_message" },
                    {
                      name: "Video Conference",
                      _id: "contact_video_conference",
                    },
                    { name: "Other", _id: "contact_other" },
                  ]}
                ></Select>
              </div>
            </div>
            <Input
              name="nextContactBody"
              label="Next Contact Note"
              rows="4"
              value={data.nextContactBody}
              onChange={handleChange}
              error={errors.nextContactBody}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-success"
            onClick={() => {
              addCaseNote();
            }}
          >
            Add Case Note
          </button>
          <button
            className="btn btn-danger"
            onClick={() => {
              setAddOpen(false);
              setIsOpen(true);
              clearData();
            }}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
      <Modal
        size="xl"
        show={isOpen}
        animation={false}
        onHide={() => {
          setIsOpen(false);
          setNotes([]);
          setLoaded(false);
          clearData();
        }}
      >
        <Modal.Header closeButton className="bg-safy-info text-white">
          <Modal.Title>{`Case Notes for ${application.HomeData[0].fostercarehomename}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row mb-2">
            <div className="col-11"></div>
            <div className="col-1">
              {JSON.stringify(safyInfo.user.safyInfo.binti) !== "[{}]" ? (
                <i
                  className="fas fa-plus-circle fa-lg text-safy-success"
                  onClick={() => {
                    setIsOpen(false);
                    setAddOpen(true);
                  }}
                />
              ) : null}
            </div>
          </div>
          {types.length > 0 ? (
            <div>
              {types.map((type, index) => (
                <button
                  className="btn btn-safy-info m-1"
                  onClick={() => setFilter(type)}
                  key={`${type}${index}`}
                >
                  {type.replaceAll("_", " ")}
                </button>
              ))}
              <button className="btn btn-danger" onClick={() => setFilter("")}>
                X
              </button>
            </div>
          ) : null}
          {notes.length > 0 ? (
            <>
              <div
                className="btn btn-secondary mt-2"
                onClick={() =>
                  window.open(
                    `https://family.binti.com/admin/adult_profiles/${applicantId}/case_notes`
                  )
                }
              >
                View/Edit Case Notes in Binti
              </div>
              <div>
                {notes
                  .filter(
                    (note) =>
                      note.attributes["case-note-type"] === filter ||
                      filter === ""
                  )
                  .map((note, index) => (
                    <div
                      className={
                        note?.relationships?.caseworker?.data?.id === "564530"
                          ? "border rounded p-2 m-2"
                          : "border border-dark rounded bg-light p-2 m-2"
                      }
                      key={`note-${note.id}-${index}`}
                    >
                      <div className="row">
                        <div
                          className="col"
                          title={`Created on ${new Date(
                            note.attributes["created-at"]
                          ).toLocaleString()}`}
                        >
                          {new Date(
                            note.attributes["date"].replaceAll("-", "/")
                          ).toDateString()}
                        </div>
                        <div className="col">
                          {`Type: ${note.attributes[
                            "case-note-type"
                          ].replaceAll("_", " ")}`}
                        </div>
                        <div className="col">
                          {note.attributes["contact-method"]
                            ? `Contact Method: ${note.attributes[
                                "contact-method"
                              ]
                                ?.replaceAll("_", " ")
                                ?.replaceAll("-", " ")
                                ?.replaceAll("contact", "")}`
                            : null}
                        </div>
                      </div>
                      <div className="fw-bold">
                        {note.attributes["case-note-body"]}
                      </div>
                    </div>
                  ))}
              </div>
            </>
          ) : loaded === true ? (
            "No Case Notes"
          ) : (
            "Loading"
          )}
        </Modal.Body>
      </Modal>
      <i
        className="far fa-sticky-note fa-lg"
        role="button"
        title="Add Case Note"
        onClick={() => showCaseNotes()}
      ></i>
    </div>
  );
}

export default BintiCaseNotes;
